import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { DEMO_SESSION_KEY } from '@/data/demoData'
import { isDemoMode, requireSupabase, supabase } from '@/lib/supabase'
import { fetchProfileById, resolveLoginEmail, verifyDemoCredentials } from '@/services/appService'
import type { AppProfile, LoginCredentials } from '@/types'

interface AuthUser {
  id: string
  email: string | null
}

interface AuthContextValue {
  user: AuthUser | null
  profile: AppProfile | null
  session: Session | null
  loading: boolean
  demoMode: boolean
  signIn: (credentials: LoginCredentials) => Promise<AppProfile>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [profile, setProfile] = useState<AppProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const loadProfile = useCallback(async (id: string) => {
    const nextProfile = await fetchProfileById(id)
    if (!nextProfile) throw new Error('No encontramos un perfil asociado a esta cuenta.')
    if (!nextProfile.active) throw new Error('inactive disabled')
    setProfile(nextProfile)
    setUser({ id: nextProfile.id, email: nextProfile.email })
    return nextProfile
  }, [])

  useEffect(() => {
    let mounted = true
    if (isDemoMode) {
      const restore = async () => {
        try {
          const id = localStorage.getItem(DEMO_SESSION_KEY)
          if (id && mounted) await loadProfile(id)
        } catch (error) {
          console.warn('No se pudo restaurar la sesión demo.', error)
          localStorage.removeItem(DEMO_SESSION_KEY)
        } finally {
          if (mounted) setLoading(false)
        }
      }
      void restore()
      return () => { mounted = false }
    }

    const client = requireSupabase()
    const restore = async () => {
      try {
        const { data, error } = await client.auth.getSession()
        if (error) throw error
        if (!mounted) return
        setSession(data.session)
        if (data.session?.user) await loadProfile(data.session.user.id)
      } catch (error) {
        console.error('No se pudo restaurar la sesión.', error)
        await client.auth.signOut()
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void restore()
    const { data: listener } = client.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return
      setSession(nextSession)
      if (event === 'SIGNED_OUT' || !nextSession?.user) {
        setUser(null)
        setProfile(null)
        return
      }
      setUser({ id: nextSession.user.id, email: nextSession.user.email ?? null })
      void loadProfile(nextSession.user.id).catch(async (error) => {
        console.error('No se pudo cargar el perfil.', error)
        await client.auth.signOut()
      })
    })
    return () => {
      mounted = false
      listener.subscription.unsubscribe()
    }
  }, [loadProfile])

  const signIn = useCallback(async ({ identifier, password }: LoginCredentials) => {
    if (isDemoMode) {
      const nextProfile = await verifyDemoCredentials(identifier, password)
      localStorage.setItem(DEMO_SESSION_KEY, nextProfile.id)
      setProfile(nextProfile)
      setUser({ id: nextProfile.id, email: nextProfile.email })
      return nextProfile
    }
    const client = requireSupabase()
    const email = await resolveLoginEmail(identifier)
    const { data, error } = await client.auth.signInWithPassword({ email, password })
    if (error) throw error
    let nextProfile: AppProfile
    try {
      nextProfile = await loadProfile(data.user.id)
    } catch (profileError) {
      await client.auth.signOut()
      throw profileError
    }
    return nextProfile
  }, [loadProfile])

  const signOut = useCallback(async () => {
    if (isDemoMode) localStorage.removeItem(DEMO_SESSION_KEY)
    else if (supabase) await supabase.auth.signOut()
    setSession(null)
    setUser(null)
    setProfile(null)
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user.id)
  }, [loadProfile, user])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    profile,
    session,
    loading,
    demoMode: isDemoMode,
    signIn,
    signOut,
    refreshProfile,
  }), [loading, profile, refreshProfile, session, signIn, signOut, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth debe utilizarse dentro de AuthProvider.')
  return context
}
